import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Box, ButtonBase, Chip, Paper, Stack, Typography } from '@mui/material'
import { timelineEntries, timelineLanes, timestamp } from './uploadTimeline'

const trackColors = { index: '#549bc2', highlight: '#dca658', play: '#88bc87' }
const entryLabel = entry => entry.type === 'index' ? 'Shift' : entry.type === 'play' ? 'AI play' : entry.highlight?.origin === 'ai' ? 'AI highlight' : 'Highlight'
const entryRange = entry => `${timestamp(entry.start)}–${timestamp(entry.end)}`

// The workspace sizes the video for a 36px track (one or two lanes; --ws-chrome in index.css).
// A third lane gets 10px more so its markers stay 12px tall; the video then gives up those 10px.
const TRACK_HEIGHT = 36
const TALL_TRACK_HEIGHT = 46
const MAX_LANES = 3

// Read by screen readers and matched by row names, but not drawn.
const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
}

export default function UploadTimeline ({ highlights, review, duration, currentTime, onSeek, onHighlight, variant = 'combined', onSelect, selectedId }) {
  const [error, setError] = useState(false)
  const [pointedId, setPointedId] = useState(null)
  const listRef = useRef(null)
  const entries = useMemo(() => timelineEntries(highlights, review), [highlights, review])
  const lanes = useMemo(() => timelineLanes(entries), [entries])
  const total = Math.max(duration || 0, review?.total_seconds || 0, ...entries.map(entry => entry.end), 1)
  const laneCount = Math.min(MAX_LANES, Math.max(1, ...lanes.map(entry => entry.lane + 1)))
  const trackHeight = laneCount > 2 ? TALL_TRACK_HEIGHT : TRACK_HEIGHT
  const pitch = Math.min(22, (trackHeight - 4) / laneCount)
  const laneTop = (trackHeight - laneCount * pitch) / 2
  // The header names the clip under the pointer or keyboard focus, otherwise the selected one.
  const readout = entries.find(entry => entry.id === pointedId) || entries.find(entry => entry.id === selectedId)

  // Keep the selected row visible in the list, which shrinks when the selected event's details open below it.
  // A row that is out of view moves to the top of the list, so the rows after it show and no row is left cut
  // through its name at the top. Only the list scrolls; the page stays where it is.
  useEffect(() => {
    if (variant === 'tracks' || !selectedId) return undefined
    const frame = window.requestAnimationFrame(() => {
      const list = listRef.current
      const row = list?.querySelector('[data-selected="true"]')
      if (!list || !row) return
      const bounds = list.getBoundingClientRect()
      const rowBounds = row.getBoundingClientRect()
      if (rowBounds.top < bounds.top || rowBounds.bottom > bounds.bottom) list.scrollTop += rowBounds.top - bounds.top
    })
    return () => window.cancelAnimationFrame(frame)
  }, [variant, selectedId, entries.length])

  const select = entry => {
    setError(!onSeek(entry.start))
    if (entry.highlight) onHighlight(entry.highlight)
    onSelect?.(entry)
  }

  return <Paper variant="outlined" component="section" aria-label={variant === 'list' ? 'Game events' : 'Upload timeline'} className={variant === 'list' ? 'editor-events-panel' : undefined} sx={{ px: 1, py: variant === 'list' ? 1 : 0.375, minWidth: 0 }}>
    {variant !== 'list' && <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 22, mb: 0.25 }}>
        <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>Timeline</Typography>
        <Typography noWrap color="text.secondary" sx={{ flex: 1, minWidth: 0, fontSize: 13 }}>
          {readout ? `${readout.title} · ${entryRange(readout)}` : ''}
        </Typography>
        <Typography color="text.secondary" className="editor-metric" sx={{ fontSize: 13 }}>{timestamp(currentTime || 0)} / {timestamp(total)}</Typography>
      </Stack>
      <Box sx={{ position: 'relative', height: trackHeight, backgroundColor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
        {lanes.map(entry => <Box component="button" key={entry.id} type="button" onClick={() => select(entry)}
          aria-label={`${entryLabel(entry)}: ${entry.title}, ${timestamp(entry.start)} to ${timestamp(entry.end)}`}
          aria-pressed={selectedId === entry.id}
          title={`${entry.title} · ${timestamp(entry.start)} to ${timestamp(entry.end)}`}
          onMouseEnter={() => setPointedId(entry.id)}
          onMouseLeave={() => setPointedId(current => current === entry.id ? null : current)}
          onFocus={() => setPointedId(entry.id)}
          onBlur={() => setPointedId(current => current === entry.id ? null : current)}
          sx={{
            position: 'absolute',
            left: `${entry.start / total * 100}%`,
            width: `${(entry.end - entry.start) / total * 100}%`,
            minWidth: 8,
            top: laneTop + Math.min(entry.lane, MAX_LANES - 1) * pitch + 1,
            height: pitch - 2,
            border: '1px solid',
            borderColor: selectedId === entry.id ? 'text.primary' : 'background.paper',
            borderRadius: 0.75,
            p: 0,
            backgroundColor: trackColors[entry.type],
            opacity: entryIsActive(entry, currentTime) || selectedId === entry.id ? 1 : 0.78,
            cursor: 'pointer',
            '&:hover, &:focus-visible': { opacity: 1, outline: '2px solid white', zIndex: 2 }
          }} />)}
        {Number.isFinite(currentTime) && <Box aria-hidden="true" sx={{ position: 'absolute', left: `${Math.min(100, currentTime / total * 100)}%`, width: 2, top: 0, bottom: 0, bgcolor: 'text.primary', pointerEvents: 'none' }} />}
      </Box>
    </>}
    {variant !== 'tracks' && <>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: variant === 'list' ? 0 : 1, px: 0.5 }}>
        <Typography variant="subtitle2">Events</Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13 }}>{entries.length}</Typography>
      </Stack>
      <Box ref={listRef} className={variant === 'list' ? 'editor-events-scroll' : undefined} sx={{ maxHeight: variant === 'list' ? undefined : 340, overflowY: 'auto', mt: 0.5 }}>
        {entries.map(entry => {
          const playerName = entry.highlight?.playerName || (entry.highlight?.playerLinked ? 'Player linked' : null)
          // Plain highlights are the common case: their colour bar marks them, and the type stays in the row's name.
          const quietType = entry.type === 'highlight' && entry.highlight?.origin !== 'ai'
          return <ButtonBase key={entry.id} onClick={() => select(entry)} aria-pressed={selectedId === entry.id}
            data-selected={selectedId === entry.id}
            className="editor-event-row"
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              px: 1,
              py: 0.75,
              borderBottom: '1px solid',
              borderBottomColor: 'divider',
              borderLeft: '3px solid',
              borderLeftColor: trackColors[entry.type],
              backgroundColor: selectedId === entry.id ? 'action.selected' : entryIsActive(entry, currentTime) ? 'action.hover' : 'transparent',
              '&:hover': { backgroundColor: 'action.hover' },
              // Keyboard focus is drawn inside the row so the list's overflow does not clip it.
              '&.Mui-focusVisible': { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: '-2px', backgroundColor: selectedId === entry.id ? 'action.selected' : 'action.hover' }
            }}>
            <Typography component="span" title={entry.title} sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              fontSize: 15,
              fontWeight: 600,
              lineHeight: '20px'
            }}>{entry.title}</Typography>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, minWidth: 0, fontSize: 13, lineHeight: '18px', color: 'text.secondary' }}>
              <Box component="span" className="editor-metric"> {entryRange(entry)}</Box>
              {playerName && <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}> · {playerName}</Box>}
              {quietType
                ? <Box component="span" sx={visuallyHidden}> {entryLabel(entry)}</Box>
                : <Chip component="span" size="small" variant="outlined" label={entryLabel(entry)} sx={{ ml: 'auto', height: 20, flexShrink: 0, borderColor: trackColors[entry.type] }} />}
            </Box>
          </ButtonBase>
        })}
        {!entries.length && <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 0.5 }}>
          Saved highlights, shift indexes, and AI plays will appear here.
        </Typography>}
      </Box>
    </>}
    {error && <Alert severity="info" sx={{ mt: 1 }}>Wait for video playback to load, then try again.</Alert>}
  </Paper>
}

function entryIsActive (entry, currentTime) {
  return Number.isFinite(currentTime) && currentTime >= entry.start && currentTime < entry.end
}

UploadTimeline.propTypes = {
  onSelect: PropTypes.func,
  selectedId: PropTypes.string,
  variant: PropTypes.oneOf(['combined', 'tracks', 'list']),
  highlights: PropTypes.array.isRequired,
  review: PropTypes.object,
  duration: PropTypes.number,
  currentTime: PropTypes.number,
  onSeek: PropTypes.func.isRequired,
  onHighlight: PropTypes.func.isRequired
}
